import { Trans } from '@lingui/macro';
import React, { useContext } from 'react';
import { getFormattedDate, getModeLabel } from '@utils/Utils';
import TooltipWrapper from '@components/tooltip';

import './style.scss';

import { BasicData } from '@App';

const TransportComponent = () => {
    const { transport_summary } = useContext(BasicData);

    const getMobileDate = (date) => date?.split(' ').slice(0, 2).join(' ') || date;

    const showDepartureTime = Object.values(transport_summary).flat().some(x => x.start_time);
    const showArrivalTime = Object.values(transport_summary).flat().some(x => x.end_time);

    // Flight
    const hasFlightAgency = transport_summary?.flights?.some(x => x.agency);
    const hasFlightRouteLabel = transport_summary?.flights?.some(x => x.route_label);
    const hasFlightClass = transport_summary?.flights?.some(x => x.class);
    const hasFlightDeparture = transport_summary?.flights?.some(x => x.from);
    const hasFlightArrival = transport_summary?.flights?.some(x => x.to);
    const hasFlightReference = transport_summary?.flights?.some(x => x.reference_codes);

    // Transfer
    const hasTransferAgency = transport_summary?.transfers?.some(x => x.agency);
    const hasTransferRouteLabel = transport_summary?.transfers?.some(x => x.route_label);
    const hasTransferDeparture = transport_summary?.transfers?.some(x => x.from);
    const hasTransferArrival = transport_summary?.transfers?.some(x => x.to);
    const hasTransferReference = transport_summary?.transfers?.some(x => x.reference_codes);

    // Car Hire
    const hasCarHireAgency = transport_summary?.car_hires?.some(x => x.agency);
    const hasCarHireVehicleClass = transport_summary?.car_hires?.some(x => x.vehicle_class);
    const hasCarHireDeparture = transport_summary?.car_hires?.some(x => x.from);
    const hasCarHireArrival = transport_summary?.car_hires?.some(x => x.to);
    const hasCarHireReference = transport_summary?.car_hires?.some(x => x.reference_codes);

    // Boats
    const hasBoatAgency = transport_summary?.boats?.some(x => x.agency);
    const hasBoatRouteLabel = transport_summary?.boats?.some(x => x.route_label);
    const hasBoatDeparture = transport_summary?.boats?.some(x => x.from);
    const hasBoatArrival = transport_summary?.boats?.some(x => x.to);
    const hasBoatReference = transport_summary?.boats?.some(x => x.reference_codes);

    // Trains
    const hasTrainAgency = transport_summary?.trains?.some(x => x.agency);
    const hasTrainRouteLabel = transport_summary?.trains?.some(x => x.route_label);
    const hasTrainDeparture = transport_summary?.trains?.some(x => x.from);
    const hasTrainArrival = transport_summary?.trains?.some(x => x.to);
    const hasTrainReference = transport_summary?.trains?.some(x => x.reference_codes);

    return (
        <div className="trip-summary-transport bg-white">
            {(transport_summary?.flights || transport_summary?.transfers) &&
                <h2 className="text-md-center mb-[1.875rem]"><Trans>Transport Summary</Trans></h2>
            }
            {transport_summary?.flights && (
                <div className="trip-summary-transport-section">
                    <h4><Trans>Flights</Trans></h4>
                    <table>
                        <thead>
                            <tr>
                                <th className="desktop"><h6><Trans>Date</Trans></h6></th>
                                {hasFlightAgency &&
                                    <th className="desktop"><h6><Trans>Airline</Trans></h6></th>
                                }
                                {hasFlightRouteLabel &&
                                    <th className="desktop"><h6><Trans>Flight</Trans></h6></th>
                                }
                                {hasFlightClass &&
                                    <th className="desktop"><h6><Trans>Ticket Class</Trans></h6></th>
                                }
                                {hasFlightDeparture &&
                                    <th className="desktop"><h6><Trans>Departure</Trans></h6></th>
                                }
                                {hasFlightArrival &&
                                    <th className="desktop"><h6><Trans>Arrival</Trans></h6></th>
                                }
                                {hasFlightReference &&
                                    <th className="desktop text-right"><h6><Trans>Reference</Trans></h6></th>
                                }
                                {(hasFlightDeparture && hasFlightArrival) &&
                                    <th className="mobile"><h6><Trans>Departure</Trans> & <Trans>Arrival</Trans></h6></th>
                                }
                                {(hasFlightAgency || hasFlightRouteLabel || hasFlightClass) &&
                                    <th className="mobile"><h6><Trans>Flight</Trans></h6></th>
                                }
                            </tr>
                        </thead>
                        <tbody>
                            {transport_summary?.flights?.map((flight, i) => {
                                const startDate = getFormattedDate(flight.start_date, null, flight.start_day);
                                const endDate = getFormattedDate(flight.end_date, null, flight.end_day);
                                const primaryReference = flight.reference_codes?.split(';').filter(Boolean)[0];
                                const otherReferences = flight.reference_codes?.split(';').filter(Boolean).slice(1);
                                return (
                                    <tr key={i}>
                                        <td className="desktop">{startDate}</td>
                                        {hasFlightAgency &&
                                            <td className="desktop break-words">{flight.agency}</td>
                                        }
                                        {hasFlightRouteLabel &&
                                            <td className="desktop break-words">
                                                <span className="d-block mb-[8px]">{flight.label ? <Trans id={flight.label} values={flight.label} /> : <Trans id={getModeLabel(flight.mode)} values={getModeLabel(flight.mode)} />}</span>
                                                <span>{flight.ticket_class && `(${flight.ticket_class})`}</span>
                                                <span>{(flight.ticket_class && flight.vehicle) && ' - '}</span>
                                                <span className="bold">{flight.vehicle}</span>
                                            </td>
                                        }
                                        {hasFlightClass &&
                                            <td className="desktop">{flight.class}</td>
                                        }
                                        {hasFlightDeparture &&
                                            <td className="desktop">
                                                <span>{flight.from}</span>
                                                {showDepartureTime && <span className="d-block bold mt-[8px]">{flight.start_time}</span>}
                                            </td>
                                        }
                                        {hasFlightArrival &&
                                            <td className="desktop">
                                                <span>{flight.to}</span>
                                                {showArrivalTime && <span className="d-block mt-[8px]"><span className='bold'>{flight.end_time}</span> {flight.overnight_travel_days && `(${flight.overnight_travel_days} day)`}</span>}
                                            </td>
                                        }
                                        {hasFlightReference &&
                                            <td className="desktop text-right break-words">
                                                {primaryReference && <span className="d-block">{primaryReference}</span>}
                                                {(otherReferences && otherReferences.length > 0) && (
                                                    <span className="w-max-content uppercase d-inline-block mt-[8px]">
                                                        <TooltipWrapper tooltipId="flight-reference-numbers-mobile" data={otherReferences}>
                                                            <span><Trans>Show More</Trans></span>
                                                        </TooltipWrapper>
                                                    </span>
                                                )}
                                            </td>
                                        }
                                        {(hasFlightDeparture && hasFlightArrival) && (
                                            <td className="mobile">
                                                <div className="departure-and-arrival">
                                                    <span className="departure">
                                                        <i className="icon icon-map-pin"></i>
                                                        {flight.from}
                                                        <span className="departure-time">{getMobileDate(startDate)} {flight.start_time && ` - ${flight.start_time}`}</span>
                                                    </span>
                                                    <span className="arrival">
                                                        <i className="icon icon-map-pin"></i>
                                                        {flight.to}
                                                        <span className="arrival-time">{getMobileDate(endDate)} {flight.end_time && ` - ${flight.end_time}`}</span>
                                                        {flight.overnight_travel_days && <span>({flight.overnight_travel_days} <Trans>day</Trans>)</span>}
                                                    </span>
                                                </div>
                                            </td>
                                        )}
                                        {(hasFlightAgency || hasFlightRouteLabel || hasFlightClass) && (
                                            <td className="mobile break-words">
                                                <span>{flight.agency} {(flight.agency && flight.ticket_class) && (
                                                    <>
                                                        {" - "}
                                                        <div className="bold inline">{flight.vehicle}</div>
                                                    </>
                                                )}
                                                </span>
                                                <span>{flight.ticket_class && `(${flight.ticket_class})`}</span>
                                                {primaryReference && <span className="flex"><Trans>Reference No</Trans>: {primaryReference}</span>}
                                                {(otherReferences && otherReferences.length > 0) && (
                                                    <span className="w-max-content uppercase d-inline-block mt-[8px]">
                                                        <TooltipWrapper tooltipId="flight-reference-numbers" data={otherReferences}>
                                                            <span><Trans>View Alternatives</Trans></span>
                                                        </TooltipWrapper>
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            {transport_summary?.transfers && (
                <div className="trip-summary-transport-section">
                    <h4><Trans>Transfers</Trans></h4>
                    <table>
                        <thead>
                            <tr>
                                <th className="desktop"><h6><Trans>Date</Trans></h6></th>
                                {hasTransferAgency &&
                                    <th className="desktop"><h6><Trans>Company</Trans></h6></th>
                                }
                                {hasTransferRouteLabel &&
                                    <th className="desktop"><h6><Trans>Type</Trans></h6></th>
                                }
                                {hasTransferDeparture &&
                                    <th className="desktop"><h6><Trans>Pick Up</Trans></h6></th>
                                }
                                {hasTransferArrival &&
                                    <th className="desktop"><h6><Trans>Drop Off</Trans></h6></th>
                                }
                                {hasTransferReference &&
                                    <th className="desktop text-right"><h6><Trans>Reference</Trans></h6></th>
                                }
                                {(hasTransferDeparture && hasTransferArrival) &&
                                    <th className="mobile"><h6><Trans>Pick Up</Trans> & <Trans>Drop Off</Trans></h6></th>
                                }
                                {(hasTransferAgency || hasTransferRouteLabel) &&
                                    <th className="mobile"><h6><Trans>Company</Trans> & <Trans>Type</Trans></h6></th>
                                }
                            </tr>
                        </thead>
                        <tbody>
                            {transport_summary?.transfers?.map((transfer, i) => {
                                const startDate = getFormattedDate(transfer.start_date, null, transfer.start_day);
                                const endDate = getFormattedDate(transfer.end_date, null, transfer.end_day);
                                const primaryReference = transfer.reference_codes?.split(';').filter(Boolean)[0];
                                const otherReferences = transfer.reference_codes?.split(';').filter(Boolean).slice(1);
                                return (
                                    <tr key={i}>
                                        <td className="desktop">{startDate}</td>
                                        {hasTransferAgency &&
                                            <td className="desktop break-words">{transfer.agency}</td>
                                        }
                                        {hasTransferRouteLabel &&
                                            <td className="desktop break-words">
                                                <span>{transfer.label ? <Trans id={getModeLabel(transfer.label)} values={getModeLabel(transfer.label)} /> : <Trans id={getModeLabel(transfer.mode)} values={getModeLabel(transfer.mode)} />}</span>
                                                <span>{(transfer.mode && transfer.vehicle) && ' - '}</span>
                                                <span>{transfer.vehicle}</span>
                                            </td>
                                        }
                                        {hasTransferDeparture &&
                                            <td className="desktop">
                                                <span>{transfer.from}</span>
                                                {showDepartureTime && <span className="d-block bold mt-[8px]">{transfer.start_time}</span>}
                                            </td>
                                        }
                                        {hasTransferArrival &&
                                            <td className="desktop">
                                                <span>{transfer.to}</span>
                                                {showArrivalTime && <span className="d-block mt-[8px]"><span className='bold'>{transfer.end_time}</span> {transfer.overnight_travel_days && `(${transfer.overnight_travel_days} day)`}</span>}
                                            </td>
                                        }
                                        {hasTransferReference &&
                                            <td className="desktop text-right break-words">
                                                {primaryReference && <span className="d-block">{primaryReference}</span>}
                                                {(otherReferences && otherReferences.length > 0) && (
                                                    <span className="w-max-content uppercase d-inline-block mt-[8px]">
                                                        <TooltipWrapper tooltipId="transfer-reference-numbers-mobile" data={otherReferences}>
                                                            <span><Trans>Show More</Trans></span>
                                                        </TooltipWrapper>
                                                    </span>
                                                )}
                                            </td>
                                        }
                                        {(hasTransferDeparture && hasTransferArrival) && (
                                            <td className="mobile">
                                                <div className="departure-and-arrival">
                                                    <span className="departure">
                                                        <i className="icon icon-map-pin"></i>
                                                        {transfer.from}
                                                        <span className="departure-time">{getMobileDate(startDate)} {transfer.start_time && ` - ${transfer.start_time}`}</span>
                                                    </span>
                                                    <span className="arrival">
                                                        <i className="icon icon-map-pin"></i>
                                                        {transfer.to}
                                                        <span className="arrival-time">{getMobileDate(endDate)} {transfer.end_time && ` - ${transfer.end_time}`}</span>
                                                        {transfer.overnight_travel_days && <span>({transfer.overnight_travel_days} <Trans>day</Trans>)</span>}
                                                    </span>
                                                </div>
                                            </td>
                                        )}
                                        {(hasTransferAgency || hasTransferRouteLabel) && (
                                            <td className="mobile break-words">
                                                <span>{transfer.agency} {(transfer.agency && transfer.vehicle) && ' - '} {transfer.vehicle && `${transfer.vehicle}`}</span>
                                                {primaryReference && <span className="flex"><Trans>Reference No</Trans>: {primaryReference}</span>}
                                                {(otherReferences && otherReferences.length > 0) && (
                                                    <span className="w-max-content uppercase d-inline-block mt-[8px]">
                                                        <TooltipWrapper tooltipId="transfer-reference-numbers" data={otherReferences}>
                                                            <span><Trans>View Alternatives</Trans></span>
                                                        </TooltipWrapper>
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            {transport_summary?.car_hires && (
                <div className="trip-summary-transport-section">
                    <h4><Trans>Car Hire</Trans></h4>
                    <table>
                        <thead>
                            <tr>
                                <th className="desktop"><h6><Trans>Date</Trans></h6></th>
                                {hasCarHireAgency &&
                                    <th className="desktop"><h6><Trans>Company</Trans></h6></th>
                                }
                                {hasCarHireVehicleClass &&
                                    <th className="desktop"><h6><Trans>Type</Trans></h6></th>
                                }
                                {hasCarHireDeparture &&
                                    <th className="desktop"><h6><Trans>Pick Up</Trans></h6></th>
                                }
                                {hasCarHireArrival &&
                                    <th className="desktop"><h6><Trans>Drop Off</Trans></h6></th>
                                }
                                {hasCarHireReference &&
                                    <th className="desktop text-right"><h6><Trans>Reference</Trans></h6></th>
                                }
                                {(hasCarHireDeparture && hasCarHireArrival) &&
                                    <th className="mobile"><h6><Trans>Pick Up</Trans> & <Trans>Drop Off</Trans></h6></th>
                                }
                                {(hasCarHireAgency || hasCarHireVehicleClass) &&
                                    <th className="mobile"><h6><Trans>Company</Trans> & <Trans>Type</Trans></h6></th>
                                }
                            </tr>
                        </thead>
                        <tbody>
                            {transport_summary?.car_hires?.map((carHire, i) => {
                                const startDate = getFormattedDate(carHire.start_date, null, carHire.start_day);
                                const endDate = getFormattedDate(carHire.end_date, null, carHire.end_day);
                                const primaryReference = carHire.reference_codes?.split(';').filter(Boolean)[0];
                                const otherReferences = carHire.reference_codes?.split(';').filter(Boolean).slice(1);
                                return (
                                    <tr key={i}>
                                        <td className="desktop">{startDate}</td>
                                        {hasCarHireAgency &&
                                            <td className="desktop break-words">{carHire.agency}</td>
                                        }
                                        {hasCarHireVehicleClass &&
                                            <td className="desktop break-words">{carHire.vehicle} {(carHire.vehicle && carHire.category) && ' - '} {carHire.category}</td>
                                        }
                                        {hasCarHireDeparture &&
                                            <td className="desktop">
                                                <span>{carHire.from}</span>
                                                {showDepartureTime && <span className="d-block bold mt-[8px]">{carHire.start_time}</span>}
                                            </td>
                                        }
                                        {hasCarHireArrival &&
                                            <td className="desktop">
                                                <span>{carHire.to}</span>
                                                {showArrivalTime && <span className="d-block bold mt-[8px]">{carHire.end_time} {(carHire.overnight_travel_days || carHire.start_date) !== carHire.end_date && `(${carHire.end_date})`}</span>}
                                            </td>
                                        }
                                        {hasCarHireReference &&
                                            <td className="desktop text-right break-words">
                                                {primaryReference && <span className="d-block">{primaryReference}</span>}
                                                {(otherReferences && otherReferences.length > 0) && (
                                                    <span className="w-max-content uppercase d-inline-block mt-[8px]">
                                                        <TooltipWrapper tooltipId="carHire-reference-numbers-mobile" data={otherReferences}>
                                                            <span><Trans>Show More</Trans></span>
                                                        </TooltipWrapper>
                                                    </span>
                                                )}
                                            </td>
                                        }
                                        {(hasCarHireDeparture && hasCarHireArrival) && (
                                            <td className="mobile">
                                                <div className="departure-and-arrival">
                                                    <span className="departure">
                                                        <i className="icon icon-map-pin"></i>
                                                        {carHire.from}
                                                        <span className="departure-time">{getMobileDate(startDate)} {carHire.start_time && ` - ${carHire.start_time}`}</span>
                                                    </span>
                                                    <span className="arrival">
                                                        <i className="icon icon-map-pin"></i>
                                                        {carHire.to}
                                                        <span className="arrival-time">{getMobileDate(endDate)} {carHire.end_time && ` - ${carHire.end_time}`}</span>
                                                        {carHire.overnight_travel_days && <span>({carHire.overnight_travel_days} <Trans>day</Trans>)</span>}
                                                    </span>
                                                </div>
                                            </td>
                                        )}
                                        {(hasCarHireAgency || hasCarHireVehicleClass) && (
                                            <td className="mobile break-words">
                                                <span>{carHire.agency} {carHire.vehicle && ` - ${carHire.vehicle}`} {carHire.category && ` - ${carHire.category}`}</span>
                                                <span>{carHire.ticket_class && `(${carHire.ticket_class})`}</span>
                                                {primaryReference && <span className="flex"><Trans>Reference No</Trans>: {primaryReference}</span>}
                                                {(otherReferences && otherReferences.length > 0) && (
                                                    <span className="w-max-content uppercase d-inline-block mt-[8px]">
                                                        <TooltipWrapper tooltipId="carHire-reference-numbers" data={otherReferences}>
                                                            <span><Trans>View Alternatives</Trans></span>
                                                        </TooltipWrapper>
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            {transport_summary?.boats && (
                <div className="trip-summary-transport-section">
                    <h4><Trans>Boats</Trans></h4>
                    <table>
                        <thead>
                            <tr>
                                <th className="desktop"><h6><Trans>Date</Trans></h6></th>
                                {hasBoatAgency &&
                                    <th className="desktop"><h6><Trans>Company</Trans></h6></th>
                                }
                                {hasBoatRouteLabel &&
                                    <th className="desktop"><h6><Trans>Type</Trans></h6></th>
                                }
                                {hasBoatDeparture &&
                                    <th className="desktop"><h6><Trans>Embark</Trans></h6></th>
                                }
                                {hasBoatArrival &&
                                    <th className="desktop"><h6><Trans>Disembark</Trans></h6></th>
                                }
                                {hasBoatReference &&
                                    <th className="desktop text-right"><h6><Trans>Reference</Trans></h6></th>
                                }
                                {(hasBoatDeparture && hasBoatArrival) &&
                                    <th className="mobile"><h6><Trans>Embark</Trans> & <Trans>Disembark</Trans></h6></th>
                                }
                                {(hasBoatAgency || hasBoatRouteLabel) &&
                                    <th className="mobile"><h6><Trans>Company</Trans> & <Trans>Type</Trans></h6></th>
                                }
                            </tr>
                        </thead>
                        <tbody>
                            {transport_summary?.boats?.map((boat, i) => {
                                const startDate = getFormattedDate(boat.start_date, null, boat.start_day);
                                const endDate = getFormattedDate(boat.end_date, null, boat.end_day);
                                const primaryReference = boat.reference_codes?.split(';').filter(Boolean)[0];
                                const otherReferences = boat.reference_codes?.split(';').filter(Boolean).slice(1);
                                return (
                                    <tr key={i}>
                                        <td className="desktop">{startDate}</td>
                                        {hasBoatAgency &&
                                            <td className="desktop break-words">{boat.agency}</td>
                                        }
                                        {hasBoatRouteLabel &&
                                            <td className="desktop break-words">
                                                <span>{boat.label ? <Trans id={boat.label} values={boat.label} /> : <Trans id={getModeLabel(boat.mode)} values={getModeLabel(boat.mode)} />}</span>
                                                <span>{(boat.mode && boat.ticket_class) && ' - '}</span>
                                                <span>{boat.ticket_class && `(${boat.ticket_class})`}</span>
                                                <span className="d-block bold">{boat.vehicle}</span>
                                            </td>
                                        }
                                        {hasBoatDeparture &&
                                            <td className="desktop">
                                                <span>{boat.from}</span>
                                                {showDepartureTime && <span className="d-block bold mt-[8px]">{boat.start_time}</span>}
                                            </td>
                                        }
                                        {hasBoatArrival &&
                                            <td className="desktop">
                                                <span>{boat.to}</span>
                                                {showArrivalTime && <span className="d-block bold mt-[8px]">{boat.end_time} {boat.overnight_travel_days}</span>}
                                            </td>
                                        }
                                        {hasBoatReference &&
                                            <td className="desktop text-right break-words">
                                                {primaryReference && <span className="d-block">{primaryReference}</span>}
                                                {(otherReferences && otherReferences.length > 0) && (
                                                    <span className="w-max-content uppercase d-inline-block mt-[8px]">
                                                        <TooltipWrapper tooltipId="boat-reference-numbers-mobile" data={otherReferences}>
                                                            <span><Trans>Show More</Trans></span>
                                                        </TooltipWrapper>
                                                    </span>
                                                )}
                                            </td>
                                        }
                                        {(hasBoatDeparture && hasBoatArrival) && (
                                            <td className="mobile">
                                                <div className="departure-and-arrival">
                                                    <span className="departure">
                                                        <i className="icon icon-map-pin"></i>
                                                        {boat.from}
                                                        <span className="departure-time">{getMobileDate(startDate)} {boat.start_time && ` - ${boat.start_time}`}</span>
                                                    </span>
                                                    <span className="arrival">
                                                        <i className="icon icon-map-pin"></i>
                                                        {boat.to}
                                                        <span className="arrival-time">{getMobileDate(endDate)} {boat.end_time && ` - ${boat.end_time}`}</span>
                                                        {boat.overnight_travel_days && <span>({boat.overnight_travel_days} <Trans>day</Trans>)</span>}
                                                    </span>
                                                </div>
                                            </td>
                                        )}
                                        {(hasBoatAgency || hasBoatRouteLabel) && (
                                            <td className="mobile break-words">
                                                <span>{boat.agency} {(boat.agency && boat.ticket_class) && ' - '}</span>
                                                <span>{boat.ticket_class && `(${boat.ticket_class})`}</span>
                                                <span className="d-block bold mb-1">{boat.vehicle}</span>
                                                {primaryReference && <span className="flex"><Trans>Reference No</Trans>: {primaryReference}</span> }
                                                {(otherReferences && otherReferences.length > 0) && (
                                                    <span className="w-max-content uppercase d-inline-block mt-[8px]">
                                                        <TooltipWrapper tooltipId="boat-reference-numbers" data={otherReferences}>
                                                            <span><Trans>View Alternatives</Trans></span>
                                                        </TooltipWrapper>
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            {transport_summary?.trains && (
                <div className="trip-summary-transport-section">
                    <h4><Trans>Trains</Trans></h4>
                    <table>
                        <thead>
                            <tr>
                                <th className="desktop"><h6><Trans>Date</Trans></h6></th>
                                {hasTrainAgency &&
                                    <th className="desktop"><h6><Trans>Company</Trans></h6></th>
                                }
                                {hasTrainRouteLabel &&
                                    <th className="desktop"><h6><Trans>Type</Trans></h6></th>
                                }
                                {hasTrainDeparture &&
                                    <th className="desktop"><h6><Trans>Embark</Trans></h6></th>
                                }
                                {hasTrainArrival &&
                                    <th className="desktop"><h6><Trans>Disembark</Trans></h6></th>
                                }
                                {hasTrainReference &&
                                    <th className="desktop text-right"><h6><Trans>Reference</Trans></h6></th>
                                }
                                {(hasTrainDeparture && hasTrainArrival) &&
                                    <th className="mobile"><h6><Trans>Embark</Trans> & <Trans>Disembark</Trans></h6></th>
                                }
                                {(hasTrainAgency || hasTrainRouteLabel) &&
                                    <th className="mobile"><h6><Trans>Company</Trans> & <Trans>Type</Trans></h6></th>
                                }
                            </tr>
                        </thead>
                        <tbody>
                            {transport_summary?.trains?.map((train, i) => {
                                const startDate = getFormattedDate(train.start_date, null, train.start_day);
                                const endDate = getFormattedDate(train.end_date, null, train.end_day);
                                const primaryReference = train.reference_codes?.split(';').filter(Boolean)[0];
                                const otherReferences = train.reference_codes?.split(';').filter(Boolean).slice(1);
                                return (
                                    <tr key={i}>
                                        <td className="desktop">{startDate}</td>
                                        {hasTrainAgency &&
                                            <td className="desktop break-words">{train.agency}</td>
                                        }
                                        {hasTrainRouteLabel &&
                                            <td className="desktop break-words">
                                                <span><Trans id={getModeLabel(train.mode)} values={getModeLabel(train.mode)} /></span>
                                                <span>{(train.mode && train.ticket_class) && ' - '}</span>
                                                <span>{train.ticket_class && `(${train.ticket_class})`}</span>
                                                <span className="d-block bold">{train.vehicle}</span>
                                            </td>
                                        }
                                        {hasTrainDeparture &&
                                            <td className="desktop">
                                                <span>{train.from}</span>
                                                {showDepartureTime && <span className="d-block bold mt-[8px]">{train.start_time}</span>}
                                            </td>
                                        }
                                        {hasTrainArrival &&
                                            <td className="desktop">
                                                <span>{train.to}</span>
                                                {showArrivalTime && <span className="d-block bold mt-[8px]">{train.end_time} {train.overnight_travel_days}</span>}
                                            </td>
                                        }
                                        {hasTrainReference &&
                                            <td className="desktop text-right break-words">
                                                {primaryReference && <span className="d-block">{primaryReference}</span>}
                                                {(otherReferences && otherReferences.length > 0) && (
                                                    <span className="w-max-content uppercase d-inline-block mt-[8px]">
                                                        <TooltipWrapper tooltipId="train-reference-numbers-mobile" data={otherReferences}>
                                                            <span><Trans>Show More</Trans></span>
                                                        </TooltipWrapper>
                                                    </span>
                                                )}
                                            </td>
                                        }
                                        {(hasTrainDeparture && hasTrainArrival) && (
                                            <td className="mobile">
                                                <div className="departure-and-arrival">
                                                    <span className="departure">
                                                        <i className="icon icon-map-pin"></i>
                                                        {train.from}
                                                        <span className="departure-time">{getMobileDate(startDate)} {train.start_time && ` - ${train.start_time}`}</span>
                                                    </span>
                                                    <span className="arrival">
                                                        <i className="icon icon-map-pin"></i>
                                                        {train.to}
                                                        <span className="arrival-time">{getMobileDate(endDate)} {train.end_time && ` - ${train.end_time}`}</span>
                                                        {train.overnight_travel_days && <span>({train.overnight_travel_days} <Trans>day</Trans>)</span>}
                                                    </span>
                                                </div>
                                            </td>
                                        )}
                                        {(hasTrainAgency || hasTrainRouteLabel) && (
                                            <td className="mobile break-words">
                                                <span>{train.agency} {(train.agency && train.ticket_class) && ' - '}</span>
                                                <span>{train.ticket_class && `(${train.ticket_class})`}</span>
                                                <span className="d-block bold mb-1">{train.vehicle}</span>
                                                {primaryReference && <span className="flex"><Trans>Reference No</Trans>: {primaryReference}</span> }
                                                {(otherReferences && otherReferences.length > 0) && (
                                                    <span className="w-max-content uppercase d-inline-block mt-[8px]">
                                                        <TooltipWrapper tooltipId="train-reference-numbers" data={otherReferences}>
                                                            <span><Trans>View Alternatives</Trans></span>
                                                        </TooltipWrapper>
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}

export default TransportComponent;